import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient, ApiError } from './client';
import { invalidateEntity, invalidateEntityDetail } from './invalidation';
import type { CompanyPolicy } from './generated';

interface Company {
  enterprise_uuid: string;
  name: string;
  slug: string;
  contact_email?: string;
  phone_number?: string;
  sector?: string;
  type_of_provision?: string;
  company_registration?: string;
  ofsted_register_number?: string;
  ofsted_report_link?: string;
  local_authority?: string;
  country?: string;
  region?: string;
  statement_of_purpose?: string;
  additional_information?: string;
  logo?: string;
  settings?: Record<string, unknown>;
  primary_color?: string;
  secondary_color?: string;
  credit_balance?: number;
  is_active?: boolean;
  status?: string;
  super_admin_name?: string;
  super_admin_email?: string;
  total_admins?: number;
  total_learners?: number;
  total_teams?: number;
  total_learning_paths?: number;
  created_at: string;
  updated_at: string;
}

interface PaginatedCompanyList {
  count: number;
  next: string | null;
  previous: string | null;
  results: Company[];
}

const COMPANY_BASE = '/companies';
// Policies live under `/c/{slug}/policies/` (not `/companies/...`) per OpenAPI.
const COMPANY_SCOPED_BASE = '/c';

export function useCompanies(
  params?: {
    page?: number;
    page_size?: number;
    search?: string;
    status?: string;
    is_active?: boolean;
    // TODO: backend does not yet honor `sector`; see backend.request.md
    sector?: string;
  },
  options?: { enabled?: boolean }
) {
  return useQuery<PaginatedCompanyList, ApiError>({
    queryKey: ['companies', 'list', params],
    queryFn: async () => {
      const response = await apiClient.get<PaginatedCompanyList>(`${COMPANY_BASE}/`, { params });
      return response.data;
    },
    enabled: options?.enabled ?? true,
  });
}

export function useCompany(id: string) {
  return useQuery<Company, ApiError>({
    queryKey: ['companies', 'detail', id],
    queryFn: async () => {
      const response = await apiClient.get<Company>(`${COMPANY_BASE}/${id}/`);
      return response.data;
    },
    enabled: !!id,
  });
}

export function useUpdateCompany(onboardingToken?: string | null) {
  const qc = useQueryClient();
  return useMutation<Company, ApiError, { id: string; data: Partial<Company> }>({
    mutationFn: async ({ id, data }) => {
      const config = onboardingToken ? { params: { token: onboardingToken } } : undefined;
      const response = await apiClient.patch<Company>(`${COMPANY_BASE}/${id}/`, data, config);
      return response.data;
    },
    onSuccess: (_data, { id }) => {
      invalidateEntity(qc, 'companies');
      invalidateEntityDetail(qc, 'companies', id);
    },
  });
}

export interface CreateCompanyPayload {
  name: string;
  contact_email: string;
  phone_number?: string;
  admin_name?: string;
  admin_role?: string;
  sector?: string;
  company_registration?: string;
  ofsted_register_number?: string;
  ofsted_report_link?: string;
  type_of_provision?: string;
  local_authority?: string;
  country?: string;
  region?: string;
  statement_of_purpose?: string;
  additional_information?: string;
}

export function useCreateCompany() {
  const qc = useQueryClient();
  return useMutation<Company, ApiError, CreateCompanyPayload>({
    mutationFn: async (data) => {
      const response = await apiClient.post<Company>(`${COMPANY_BASE}/`, data);
      return response.data;
    },
    onSuccess: () => {
      invalidateEntity(qc, 'companies');
    },
  });
}

export function useDeleteCompany() {
  const qc = useQueryClient();
  return useMutation<void, ApiError, string>({
    mutationFn: async (slug) => {
      await apiClient.delete(`${COMPANY_BASE}/${slug}/`);
    },
    onSuccess: () => {
      invalidateEntity(qc, 'companies');
    },
  });
}

// ---- Company policies / resources -------------------------------------------

/**
 * A company resource (policy/document) with its per-course links (BE-E6.1). The
 * generated `CompanyPolicy` lacks `course_keys`, so we extend it here. Course
 * keys are opaque strings matching `CourseAllocation.course_id`.
 */
export interface CompanyPolicyWithCourses extends CompanyPolicy {
  course_keys: string[];
}

export interface PaginatedCompanyPolicyWithCoursesList {
  count: number;
  next: string | null;
  previous: string | null;
  results: CompanyPolicyWithCourses[];
}

export interface CompanyPoliciesParams {
  /** Filter to resources linked to this course (the "surface at allocation" hook). */
  course_key?: string;
}

export function useCompanyPolicies(
  companySlug: string | undefined,
  params?: CompanyPoliciesParams
) {
  return useQuery<PaginatedCompanyPolicyWithCoursesList, ApiError>({
    queryKey: ['companies', companySlug, 'policies', params ?? {}],
    queryFn: async () => {
      const response = await apiClient.get<PaginatedCompanyPolicyWithCoursesList>(
        `${COMPANY_SCOPED_BASE}/${companySlug}/policies/`,
        { params }
      );
      return response.data;
    },
    enabled: !!companySlug,
  });
}

export function useUploadCompanyPolicy(companySlug: string | undefined) {
  const qc = useQueryClient();
  return useMutation<CompanyPolicyWithCourses, ApiError, File>({
    mutationFn: async (file) => {
      const formData = new FormData();
      formData.append('file', file);
      const response = await apiClient.post<CompanyPolicyWithCourses>(
        `${COMPANY_SCOPED_BASE}/${companySlug}/policies/`,
        formData
      );
      return response.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['companies', companySlug, 'policies'] });
    },
  });
}

export function useDeleteCompanyPolicy(companySlug: string | undefined) {
  const qc = useQueryClient();
  return useMutation<void, ApiError, number>({
    mutationFn: async (id) => {
      await apiClient.delete(`${COMPANY_SCOPED_BASE}/${companySlug}/policies/${id}/`);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['companies', companySlug, 'policies'] });
    },
  });
}

/**
 * Link one or more courses to a resource (BE-E6.1,
 * `POST /c/{slug}/policies/{id}/courses/`). Idempotent server-side; returns the
 * updated resource with its full `course_keys`.
 */
export function useLinkPolicyCourses(companySlug: string | undefined) {
  const qc = useQueryClient();
  return useMutation<
    CompanyPolicyWithCourses,
    ApiError,
    { policyId: number; courseKeys: string[] }
  >({
    mutationFn: async ({ policyId, courseKeys }) => {
      const response = await apiClient.post<CompanyPolicyWithCourses>(
        `${COMPANY_SCOPED_BASE}/${companySlug}/policies/${policyId}/courses/`,
        { course_keys: courseKeys }
      );
      return response.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['companies', companySlug, 'policies'] });
    },
  });
}

/**
 * Unlink one course from a resource (BE-E6.1,
 * `DELETE /c/{slug}/policies/{id}/courses/{course_key}/`). The course key is a
 * path segment (it can contain slashes/colons), so it's URL-encoded.
 */
export function useUnlinkPolicyCourse(companySlug: string | undefined) {
  const qc = useQueryClient();
  return useMutation<void, ApiError, { policyId: number; courseKey: string }>({
    mutationFn: async ({ policyId, courseKey }) => {
      await apiClient.delete(
        `${COMPANY_SCOPED_BASE}/${companySlug}/policies/${policyId}/courses/${encodeURIComponent(
          courseKey
        )}/`
      );
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['companies', companySlug, 'policies'] });
    },
  });
}

export type { Company, PaginatedCompanyList, CompanyPolicy };
